import { useState } from "react";
import {
  Plus,
  Search,
  Building2,
  Mail,
  Phone,
  Pencil,
  Trash2,
} from "lucide-react";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OrganizerForm } from "@/components/Organizers/OrganizerForm";
import {
  useOrganizers,
  useDeleteOrganizer,
  type Organizer,
} from "@/hooks/useOrganizers";
import { useToast } from "@/hooks/useToast";

export function OrganizersPage() {
  const { data: organizers = [], isLoading } = useOrganizers();
  const deleteOrganizer = useDeleteOrganizer();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrganizer, setEditingOrganizer] = useState<Organizer | null>(
    null,
  );
  const [deletingOrganizer, setDeletingOrganizer] = useState<Organizer | null>(
    null,
  );

  const handleEdit = (org: Organizer) => {
    setEditingOrganizer(org);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingOrganizer(null);
  };

  const handleDelete = async () => {
    if (!deletingOrganizer) return;
    try {
      await deleteOrganizer.mutateAsync(deletingOrganizer.id);
      toast({ title: "Organisateur supprimé" });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'organisateur",
        variant: "destructive",
      });
    } finally {
      setDeletingOrganizer(null);
    }
  };

  const filteredOrganizers = organizers.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.category.toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header title="Organisateurs" />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher un organisateur..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Button
            onClick={() => setFormOpen(true)}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvel organisateur
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredOrganizers.map((org) => (
            <Card key={org.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{org.name}</CardTitle>
                    <CardDescription>{org.contact_person}</CardDescription>
                  </div>
                  <Badge variant="secondary">{org.category}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {org.email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>{org.email}</span>
                  </div>
                )}
                {org.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{org.phone}</span>
                  </div>
                )}
                {org.address && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    <span className="line-clamp-1">{org.address}</span>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(org)}
                  >
                    <Pencil className="mr-1 h-3 w-3" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeletingOrganizer(org)}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredOrganizers.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            Aucun organisateur trouvé
          </div>
        )}
      </div>

      <OrganizerForm
        open={formOpen}
        onClose={handleCloseForm}
        organizer={editingOrganizer ?? undefined}
      />

      <AlertDialog
        open={!!deletingOrganizer}
        onOpenChange={(open) => !open && setDeletingOrganizer(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'organisateur</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{deletingOrganizer?.name}" ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
